import { Add as AddIcon, Email as EmailIcon, Remove as RemoveIcon } from '@mui/icons-material'
import { Button, IconButton, InputBase, Stack } from '@mui/material'
import { useAssignDocuments } from '@src/api/document-api'
import { useUser } from '@src/providers/UserProvider'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

const AssignSection = () => {
  const { t } = useTranslation()
  const user = useUser()
  const assignMutation = useAssignDocuments()
  const [count, setCount] = useState(3)

  const handleAssign = async () => {
    if (!user?.name) return
    try {
      const result = await assignMutation.mutateAsync({ count })
      enqueueSnackbar(t('devtools.assignSuccess', { count: result.length }), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t('devtools.assignError'), { variant: 'error' })
      console.error(error)
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0} sx={{ height: 32 }}>
      <Button
        variant="contained"
        size="small"
        startIcon={<EmailIcon />}
        onClick={handleAssign}
        disabled={assignMutation.isPending || !user?.name}
        data-tour="assign-button"
        sx={{
          whiteSpace: 'nowrap',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          height: '100%',
        }}
      >
        {t('devtools.assignEmailsToMe', { count })}
      </Button>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          border: 1,
          borderColor: 'primary.main',
          borderLeft: 0,
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
          height: '100%',
        }}
      >
        <IconButton size="small" onClick={() => setCount((c) => Math.max(1, c - 1))} disabled={count <= 1} sx={{ borderRadius: 0 }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <InputBase
          value={count}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10)
            if (!isNaN(v)) setCount(clamp(v, 1, 99))
          }}
          inputProps={{
            type: 'number',
            min: 1,
            max: 99,
            style: { textAlign: 'center', width: 28, padding: 0, MozAppearance: 'textfield' },
          }}
          sx={{
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none' },
          }}
        />
        <IconButton size="small" onClick={() => setCount((c) => Math.min(99, c + 1))} disabled={count >= 99} sx={{ borderRadius: 0 }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  )
}

export default AssignSection
